import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box, Stack, Skeleton, Typography } from "@mui/material";
import clsx from "clsx";
import {useDispatch, useSelector} from "react-redux";
import {useOutletContext} from "react-router-dom";

import styles from "./LiveTV.module.css";
import {CustomInput, CustomTabs, Loader, MovieCard} from "../../components";
import {AppDispatch, RootState} from "../../store";
import {setActiveTV, setListTV} from "../../store/activeTV/activeTVSlice";
import {useTranslation} from "react-i18next";
import useResize from "../../hooks/useResize";
import {
    IDataResponse,
    useFetchCategoriesRequestQuery,
    useLazyGetAllContentQuery,
    useLazyGetPageContentQuery
} from "../../api/modules";
import {TABLET_VERSION} from "../../constants";
import {useUserRole} from "../../hooks/useUserRole";
import helper from "../../constants/helper";
import { LayoutOutletContext } from "../PageOutlet/PageOutlet";
import {
    FAVORITES_CATEGORY_ID,
    LIVE_FAVORITES_UPDATED_EVENT,
    LIVE_TV_PAGE_NAME,
    LIVE_TV_REGION_BY_INDEX,
    LIVE_TV_REGIONS,
    LOAD_MORE_SKELETON_COUNT
} from "../../constants";
import {useLoadMoreSentinel} from "../../hooks/useLoadMoreSentinel";

const LiveTv = () => {
    const {t} = useTranslation("translations");
    const {innerWidth} = useResize();
    const dispatch: AppDispatch = useDispatch();
    const role = useUserRole();

    const isMobile = innerWidth < TABLET_VERSION;

    const PROFILE_ID = useMemo(() => Number(localStorage.getItem(helper.EPIC_PROFILE_ID)), []);

    const {selectedTV} = useSelector((state: RootState) => state.tvContent);

    const [data, setData] = useState<IDataResponse[]>([]);
    const [isFetchingContent, setIsFetchingContent] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState<string>(LIVE_TV_REGIONS.MEXICO);
    const [selectedRegionTab, setSelectedRegionTab] = useState<number>(0);
    const [selectedCategoryByRegion, setSelectedCategoryByRegion] = useState<Record<string, number>>({
        [LIVE_TV_REGIONS.MEXICO]: 0,
        [LIVE_TV_REGIONS.USA]: 0,
        [LIVE_TV_REGIONS.COSTA_RICA]: 0,
    });
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const selectedCategoryByRegionRef = useRef(selectedCategoryByRegion);
    const dataRef = useRef<IDataResponse[]>([]);
    const requestKeyRef = useRef("");
    const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
    const isLoadMoreRequestedRef = useRef(false);

    const {setLayoutOptions, resetLayoutOptions} =
        useOutletContext<LayoutOutletContext>();

    const isWithoutMenu = Boolean(isMobile && selectedTV);

    const regionTabs = useMemo(() => [
        { id: 0, name: "Mexico" },
        { id: 1, name: "Estados Unidos" },
        { id: 2, name: "Costa Rica" }
    ], []);
    const loadMoreSkeletons = useMemo(() => Array.from({length: LOAD_MORE_SKELETON_COUNT}), []);

    useEffect(() => {
        setLayoutOptions({withoutMenu: isWithoutMenu});
        return () => resetLayoutOptions();
    }, [isWithoutMenu, setLayoutOptions, resetLayoutOptions]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery])

    const { data: list, isFetching: isCategoriesFetching } = useFetchCategoriesRequestQuery({
        page: LIVE_TV_PAGE_NAME,
        type: "category_list",
        role,
        region: selectedRegion,
        profile_id: PROFILE_ID,
    });

    const [fetchAllTvContent] = useLazyGetAllContentQuery();
    const [fetchContent] = useLazyGetPageContentQuery();
    const selectedCategoryTab = selectedCategoryByRegion[selectedRegion] ?? 0;

    useEffect(() => {
        selectedCategoryByRegionRef.current = selectedCategoryByRegion;
    }, [selectedCategoryByRegion]);

        useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const fetchCategoryPage = useCallback(
        async ({
            categoryId,
            page,
            append,
            requestKey,
            search = "",
        }: {
            categoryId: number;
            page: number;
            append: boolean;
            requestKey: string;
            search?: string;
        }) => {
            const normalizedSearch = search.trim();
            const isSearching = normalizedSearch.length > 0;
            const shouldUseAllEndpoint = isSearching || categoryId === 0;
            const response =
                shouldUseAllEndpoint
                    ? await fetchAllTvContent({
                          page: LIVE_TV_PAGE_NAME,
                          role,
                          page_number: page,
                          rehydrated_pagination: true,
                          search: normalizedSearch,
                          ...(isSearching
                              ? {}
                              : {
                                    region: selectedRegion,
                                    profile_id: PROFILE_ID,
                                }),
                      }).unwrap()
                    : await fetchContent({
                          page: LIVE_TV_PAGE_NAME,
                          role,
                          content_id: categoryId,
                          profile_id: PROFILE_ID,
                          region: selectedRegion,
                          page_number: page,
                          rehydrated_pagination: true,
                          search
                      }).unwrap();

            if (requestKeyRef.current !== requestKey) {
                return;
            }

            const results = response?.results ?? [];
            const mergedData = append ? [...dataRef.current, ...results] : results;

            setData(mergedData);
            dataRef.current = mergedData;
            dispatch(setListTV(mergedData as any));

            if (!append) {
                dispatch(setActiveTV(mergedData[0] as any));
            }

            setCurrentPage(page);
            setHasMorePages(Boolean(response?.next));
        },
        [fetchAllTvContent, fetchContent, role, PROFILE_ID, selectedRegion, dispatch]
    );

    const handleTabChange = useCallback(
        async (categoryId: number) => {
            const requestKey = `${selectedRegion}:${categoryId}`;
            requestKeyRef.current = requestKey;

            setSelectedCategoryByRegion((prev) => ({
                ...prev,
                [selectedRegion]: categoryId,
            }));
            setIsFetchingContent(true);
            setCurrentPage(1);
            setHasMorePages(false);
            isLoadMoreRequestedRef.current = false;

            try {
                await fetchCategoryPage({
                    categoryId,
                    page: 1,
                    append: false,
                    requestKey,
                    search: debouncedSearch,
                });
            } catch (e) {
                void e;
            } finally {
                setIsFetchingContent(false);
            }
        },
        [debouncedSearch, fetchCategoryPage, selectedRegion]
    );

    useEffect(() => {
        const cachedCategoryTab =
            selectedCategoryByRegionRef.current[selectedRegion] ?? 0;
        const hasCategory = list?.some((item) => item.id === cachedCategoryTab);
        const nextCategoryId =
            cachedCategoryTab === 0 || hasCategory ? cachedCategoryTab : 0;

        if (nextCategoryId !== cachedCategoryTab) {
            setSelectedCategoryByRegion((prev) => ({
                ...prev,
                [selectedRegion]: 0,
            }));
        }

        handleTabChange(nextCategoryId);
    }, [selectedRegion, list, handleTabChange]);

    useEffect(() => {
        if (!list) return;
        const requestKey = `${selectedRegion}:${selectedCategoryTab}:${debouncedSearch}`;
        requestKeyRef.current = requestKey;
        setIsFetchingContent(true);
        setCurrentPage(1);
        setHasMorePages(false);
        isLoadMoreRequestedRef.current = false;

        fetchCategoryPage({
            categoryId: selectedCategoryTab,
            page: 1,
            append: false,
            requestKey,
            search: debouncedSearch,
        })
        .catch(() => void 0)
        .finally(() => setIsFetchingContent(false));

    }, [debouncedSearch, fetchCategoryPage, list, selectedRegion, selectedCategoryTab]);

    useEffect(() => {
        const handleLiveFavoritesUpdated = () => {
            if (!list || selectedCategoryTab !== FAVORITES_CATEGORY_ID) return;

            const requestKey = `${selectedRegion}:${selectedCategoryTab}:${debouncedSearch}:favorites-refresh`;
            requestKeyRef.current = requestKey;
            setCurrentPage(1);
            setHasMorePages(false);
            isLoadMoreRequestedRef.current = false;

            fetchCategoryPage({
                categoryId: selectedCategoryTab,
                page: 1,
                append: false,
                requestKey,
                search: debouncedSearch,
            }).catch(() => void 0);
        };

        window.addEventListener(LIVE_FAVORITES_UPDATED_EVENT, handleLiveFavoritesUpdated);
        return () => {
            window.removeEventListener(LIVE_FAVORITES_UPDATED_EVENT, handleLiveFavoritesUpdated);
        };
    }, [debouncedSearch, fetchCategoryPage, list, selectedCategoryTab, selectedRegion]);

    const handleLoadMore = useCallback(async () => {
        if (!hasMorePages || isFetchingContent || isLoadingMore || isLoadMoreRequestedRef.current) {
            return;
        }

        isLoadMoreRequestedRef.current = true;
        setIsLoadingMore(true);

        try {
            const nextPage = currentPage + 1;
            const requestKey = `${selectedRegion}:${selectedCategoryTab}:${debouncedSearch}`;

            await fetchCategoryPage({
                categoryId: selectedCategoryTab,
                page: nextPage,
                append: true,
                requestKey,
                search: debouncedSearch,
            });
        } catch (e) {
            void e;
        } finally {
            setIsLoadingMore(false);
            isLoadMoreRequestedRef.current = false;
        }
    }, [
        hasMorePages,
        isFetchingContent,
        isLoadingMore,
        currentPage,
        selectedRegion,
        selectedCategoryTab,
        debouncedSearch,
        fetchCategoryPage,
    ]);

    useLoadMoreSentinel({
        sentinelRef: loadMoreSentinelRef,
        onLoadMore: handleLoadMore,
    });

    const handleRegionChange = (index: number) => {
        setSelectedRegionTab(index);
        const region = LIVE_TV_REGION_BY_INDEX[index] ?? LIVE_TV_REGIONS.MEXICO;
        setSelectedRegion(region);
    };

    if ((!list && isCategoriesFetching) || (isFetchingContent && data.length === 0)) {
        return <Loader className={styles.loader} />;
    }

    const cardsContent = (
        <>
            <div className={styles.search}>
                <CustomInput
                    startIcon
                    name="search"
                    placeholder={t("Aside.search")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>
            <Box mb={2}>
                <CustomTabs
                    list={regionTabs}
                    setSelected={handleRegionChange}
                    selectedValue={selectedRegionTab}
                    withoutAll
                    className="MuiTab-region"
                />
            </Box>

            <Box mb={2}>
                <CustomTabs
                    list={list || []}
                    categories
                    setSelected={handleTabChange}
                    selectedValue={selectedCategoryTab}
                />
            </Box>

                <Stack direction="row" flexWrap="wrap" justifyContent="center" gap="1rem" mt={5}>
                    {data?.map((e, i) => (
                    <MovieCard
                        key={`${e.id}-${i}`}
                        data={e}
                        isProgram
                        directPlay
                        contentContext={{
                            page: "LIVE",
                            categoryId: selectedCategoryTab,
                            region: selectedRegion,
                        }}
                    />
                ))}
            </Stack>

            <Box ref={loadMoreSentinelRef} sx={{height: "1px", width: "100%"}}/>

            {isLoadingMore && (
                <Stack direction="row" flexWrap="wrap" justifyContent="center" gap="1rem" mt={2}>
                    {loadMoreSkeletons.map((_, index) => (
                        <Skeleton
                            key={`live-tv-load-more-skeleton-${index}`}
                            variant="rectangular"
                            animation="wave"
                            className={styles.loadMoreSkeletonCard}
                        />
                    ))}
                </Stack>
            )}
        </>
    );

    return (
        <>
            {!isMobile && list ? (
                <Box className={styles.wrapper}>
                    <Typography variant="h4" className={styles.title}>
                        {t("Aside.liveTV")}
                    </Typography>
                    {cardsContent}
                </Box>
            ) : (
                <Box className={clsx(isMobile && styles.contentMobile)}>
                    {list && (
                        <Box className={styles.wrapper}>
                            <Typography variant="h4" className={styles.title}>
                                {t("Aside.liveTV")}
                            </Typography>
                        {cardsContent}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

export default LiveTv;
